import Head from 'next/head';
import useSWR, { SWRConfig } from 'swr';
import { Box, Container, Stack, Text, useToast } from '@chakra-ui/react';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import UserContainer from '@containers/UserContainer';
import useOpenGraphLocale from '@lib/hooks/useOpenGraphLocale';

import { getUserByUsername } from '../api/user';

const fetcher = (url: string) => fetch(url).then((r) => r.json());

function UserHomepage() {
  const toast = useToast();
  const { t } = useTranslation('homepage');
  const openGraphLocale = useOpenGraphLocale();

  const router = useRouter();
  const { username } = router.query;

  const { data } = useSWR(`/api/user?username=${username}`, fetcher);

  const { name, bio, coverPhoto } = data;

  useEffect(() => {
    if (data && !data.name) {
      toast({
        title: t("User haven't create his/her page"),
        status: 'error',
        onCloseComplete: () => (window.location.href = '/'),
      });
    }
  }, [data, toast, t]);

  return (
    <>
      <Head>
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="theme-color" content="#5bbad5" />
      </Head>
      <NextSeo
        title={name}
        description={bio}
        canonical={`https://nutshell.live/@${username}`}
        openGraph={{
          title: `${name} - Nutshell Live`,
          locale: openGraphLocale,
          description: bio,
          url: `https://nutshell.live/@${username}`,
          site_name: 'Nutshell Live',
          images: [
            {
              url: coverPhoto || null,
              width: 1200,
              height: 630,
              alt: `${name} - Nutshell Live`,
            },
          ],
        }}
      />
      <UserContainer {...data} />

      <Box color="gray.400">
        <Container
          as={Stack}
          maxW={'xl'}
          py={8}
          direction={{ base: 'column', md: 'row' }}
          spacing={4}
          justify={{ base: 'center', md: 'center' }}
          align={{ base: 'center', md: 'center' }}
        >
          <Text>
            © 2021{' '}
            <a
              href="https://nutshell.live/?ref=brand"
              target="_blank"
              rel="noreferrer"
            >
              Nutshell Live
            </a>
          </Text>
        </Container>
      </Box>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const { username } = query;

  const user = await getUserByUsername(username as string);

  return {
    props: {
      fallback: {
        [`/api/user?username=${username}`]: user,
      },
    },
  };
};

const OverrideSWRConfig: TempFixFCPropsType = SWRConfig;

const Page = ({
  fallback,
}: {
  fallback:
    | {
        [key: string]: any;
      }
    | undefined;
}) => {
  return (
    <OverrideSWRConfig value={{ fallback }}>
      <UserHomepage />
    </OverrideSWRConfig>
  );
};

export default Page;
